import { memo, useEffect, useRef } from 'react'
import { BrowserRouter as Router, useHistory, Redirect, Route, Switch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { historyWatcherSelector } from 'selectors'
import SignOut from 'components/pages/SignOut'
import Loading from 'components/pages/Loading'

import DriveRouter from 'routers/Drive'
import Gtm from 'util/gtm'
import { sendNativeMessage } from 'util/native'
import InsurancePollingProvider from 'context/InsurancePollingContext'

const reactAppPath = import.meta.env.REACT_APP_BASENAME ? `/${import.meta.env.REACT_APP_BASENAME}` : ''
const webview = window.location.pathname.startsWith(`${reactAppPath}/webview`)

const RootRouter = memo((props) => {
  const dispatch = useDispatch()
  const isWebview = webview || (props.startRoute && props.startRoute.startsWith('/webview'))

  useEffect(() => {
    if (isWebview) {
      dispatch({ type: 'IS_WEBVIEW' })
    }
  }, [dispatch, isWebview])

  return (
    <Router basename={reactAppPath || undefined} forceRefresh={false}>
      <>
        <HistoryWatcher />
        <Switch>
          <Route path="/sign-out" component={SignOut} />
          <Route path="/loading" component={Loading} />
          <Route path="/">
            <InsurancePollingProvider>
              <DriveRouter isWebview={isWebview} />
            </InsurancePollingProvider>
          </Route>
        </Switch>
        {props.startRoute && <Redirect to={props.startRoute} />}
      </>
    </Router>
  )
})

function sendDataToGTM(profile) {
  if (profile) {
    const memberType = 'driver'
    const basic_driver = profile.certifications.includes('basic_driver')
    const basic_sender = profile.certifications.includes('basic_sender')
    Gtm.push({
      event: 'user_id',
      user_id: profile.id,
      member_type: memberType,
      basic_driver,
      basic_sender,
    })
  }
}

const HistoryWatcher = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { profile } = useSelector(historyWatcherSelector)

  // The history listener is registered once but must read the latest profile
  const profileRef = useRef(profile)
  profileRef.current = profile

  useEffect(() => {
    sendDataToGTM(profileRef.current)
    const unlisten = history.listen((location) => {
      // THEN THE PAGE CHANGES ... //

      sendNativeMessage({ type: 'NAVIGATION', url: window.location.origin + reactAppPath + location.pathname })
      // Close the sidebar
      dispatch({ type: 'SET_SIDEBAR_OPEN', open: false })

      // Scroll to the top
      document.body.scrollTop = document.documentElement.scrollTop = 0

      if (location.state && location.state.flash) {
        dispatch({ type: 'PUSH_FLASH', flash: location.state.flash })
      } else {
        dispatch({ type: 'CLEAR_ALL_FLASH' })
      }

      // Send user to GTM
      sendDataToGTM(profileRef.current)
    })

    return unlisten
  }, [dispatch, history])

  return null
}

export default RootRouter
